import * as z from 'zod';

export const CreateBranchRequest = z.object({
  branch: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Branch ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the branch is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the branch.'
          )
          .optional()
          .nullable(),
        type: z.enum(['USER', 'TECHNICAL', 'ORIGINAL_BRANCH']).optional(),
        name: z.string().describe('Branch name.').max(50).optional().nullable(),
        sourceType: z.enum(['SOURCE_BRANCH']).optional(),
        lastPublishDate: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe('Date and time the branch was last published.')
          .optional()
          .nullable(),
        editorType: z
          .object({
            editorTypeOptions: z.enum(['STUDIO', 'CLASSIC']).optional(),
          })
          .describe('Wix editor type.')
          .optional(),
        default: z
          .boolean()
          .describe(
            'Whether this branch is the default branch. The default branch is used for API operations when no branch ID is specified.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the branch was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the branch was last updated.')
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for the branch.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        tags: z
          .object({
            tags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe('Public tags assigned to the branch.')
          .optional(),
      }),
      z.xor([
        z.object({
          sourceBranchProperties: z.never().optional(),
          sourceTemplateProperties: z.never().optional(),
        }),
        z.object({
          sourceTemplateProperties: z.never().optional(),
          sourceBranchProperties: z
            .object({
              branchId: z
                .string()
                .describe('Source branch ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              branchRevision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number of the source branch when this branch was created. To prevent conflicting changes, the current revision must be passed when updating the branch.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Source branch properties. Use when `sourceType` = `SOURCE_BRANCH`.'
            ),
        }),
        z.object({
          sourceBranchProperties: z.never().optional(),
          sourceTemplateProperties: z
            .object({
              templateId: z
                .string()
                .describe('Source template ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
            })
            .describe(
              'Source template properties. Use when `sourceType` = `SOURCE_TEMPLATE`.'
            ),
        }),
      ])
    )
    .describe('Branch to create.'),
});
export const CreateBranchResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Branch ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'Revision number, which increments by 1 each time the branch is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the branch.'
      )
      .optional()
      .nullable(),
    type: z
      .enum(['USER', 'TECHNICAL', 'ORIGINAL_BRANCH'])
      .describe('Branch type.')
      .optional(),
    name: z.string().describe('Branch name.').max(50).optional().nullable(),
    sourceType: z
      .enum(['SOURCE_BRANCH'])
      .describe(
        "Source type for this branch's content. Must be aligned with the corresponding source properties below."
      )
      .optional(),
    lastPublishDate: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe('Date and time the branch was last published.')
      .optional()
      .nullable(),
    editorType: z
      .object({
        editorTypeOptions: z
          .enum(['STUDIO', 'CLASSIC'])
          .describe('Wix editor types.')
          .optional(),
      })
      .describe('Wix editor type.')
      .optional(),
    default: z
      .boolean()
      .describe(
        'Whether this branch is the default branch. The default branch is used for API operations when no branch ID is specified.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the branch was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the branch was last updated.')
      .optional()
      .nullable(),
    extendedFields: z
      .object({
        namespaces: z
          .record(z.string(), z.record(z.string(), z.any()))
          .describe(
            'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
          )
          .optional(),
      })
      .describe(
        'Custom field data for the branch.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
      )
      .optional(),
    tags: z
      .object({
        tags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
          )
          .optional(),
      })
      .describe('Public tags assigned to the branch.')
      .optional(),
  }),
  z.xor([
    z.object({
      sourceBranchProperties: z.never().optional(),
      sourceTemplateProperties: z.never().optional(),
    }),
    z.object({
      sourceTemplateProperties: z.never().optional(),
      sourceBranchProperties: z
        .object({
          branchId: z
            .string()
            .describe('Source branch ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          branchRevision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number of the source branch when this branch was created. To prevent conflicting changes, the current revision must be passed when updating the branch.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Source branch properties. Use when `sourceType` = `SOURCE_BRANCH`.'
        ),
    }),
    z.object({
      sourceBranchProperties: z.never().optional(),
      sourceTemplateProperties: z
        .object({
          templateId: z
            .string()
            .describe('Source template ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
        })
        .describe(
          'Source template properties. Use when `sourceType` = `SOURCE_TEMPLATE`.'
        ),
    }),
  ])
);
export const GetBranchRequest = z.object({
  branchId: z
    .string()
    .describe('ID of the branch to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetBranchResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Branch ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'Revision number, which increments by 1 each time the branch is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the branch.'
      )
      .optional()
      .nullable(),
    type: z
      .enum(['USER', 'TECHNICAL', 'ORIGINAL_BRANCH'])
      .describe('Branch type.')
      .optional(),
    name: z.string().describe('Branch name.').max(50).optional().nullable(),
    sourceType: z
      .enum(['SOURCE_BRANCH'])
      .describe(
        "Source type for this branch's content. Must be aligned with the corresponding source properties below."
      )
      .optional(),
    lastPublishDate: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe('Date and time the branch was last published.')
      .optional()
      .nullable(),
    editorType: z
      .object({
        editorTypeOptions: z
          .enum(['STUDIO', 'CLASSIC'])
          .describe('Wix editor types.')
          .optional(),
      })
      .describe('Wix editor type.')
      .optional(),
    default: z
      .boolean()
      .describe(
        'Whether this branch is the default branch. The default branch is used for API operations when no branch ID is specified.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the branch was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the branch was last updated.')
      .optional()
      .nullable(),
    extendedFields: z
      .object({
        namespaces: z
          .record(z.string(), z.record(z.string(), z.any()))
          .describe(
            'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
          )
          .optional(),
      })
      .describe(
        'Custom field data for the branch.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
      )
      .optional(),
    tags: z
      .object({
        tags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
          )
          .optional(),
      })
      .describe('Public tags assigned to the branch.')
      .optional(),
  }),
  z.xor([
    z.object({
      sourceBranchProperties: z.never().optional(),
      sourceTemplateProperties: z.never().optional(),
    }),
    z.object({
      sourceTemplateProperties: z.never().optional(),
      sourceBranchProperties: z
        .object({
          branchId: z
            .string()
            .describe('Source branch ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          branchRevision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number of the source branch when this branch was created. To prevent conflicting changes, the current revision must be passed when updating the branch.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Source branch properties. Use when `sourceType` = `SOURCE_BRANCH`.'
        ),
    }),
    z.object({
      sourceBranchProperties: z.never().optional(),
      sourceTemplateProperties: z
        .object({
          templateId: z
            .string()
            .describe('Source template ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
        })
        .describe(
          'Source template properties. Use when `sourceType` = `SOURCE_TEMPLATE`.'
        ),
    }),
  ])
);
export const UpdateBranchRequest = z.object({
  _id: z
    .string()
    .describe('Branch ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  branch: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Branch ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the branch is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the branch.'
          ),
        type: z.enum(['USER', 'TECHNICAL', 'ORIGINAL_BRANCH']).optional(),
        name: z.string().describe('Branch name.').max(50).optional().nullable(),
        sourceType: z.enum(['SOURCE_BRANCH']).optional(),
        lastPublishDate: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe('Date and time the branch was last published.')
          .optional()
          .nullable(),
        editorType: z
          .object({
            editorTypeOptions: z.enum(['STUDIO', 'CLASSIC']).optional(),
          })
          .describe('Wix editor type.')
          .optional(),
        default: z
          .boolean()
          .describe(
            'Whether this branch is the default branch. The default branch is used for API operations when no branch ID is specified.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the branch was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the branch was last updated.')
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for the branch.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        tags: z
          .object({
            tags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe('Public tags assigned to the branch.')
          .optional(),
      }),
      z.xor([
        z.object({
          sourceBranchProperties: z.never().optional(),
          sourceTemplateProperties: z.never().optional(),
        }),
        z.object({
          sourceTemplateProperties: z.never().optional(),
          sourceBranchProperties: z
            .object({
              branchId: z
                .string()
                .describe('Source branch ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              branchRevision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number of the source branch when this branch was created. To prevent conflicting changes, the current revision must be passed when updating the branch.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Source branch properties. Use when `sourceType` = `SOURCE_BRANCH`.'
            ),
        }),
        z.object({
          sourceBranchProperties: z.never().optional(),
          sourceTemplateProperties: z
            .object({
              templateId: z
                .string()
                .describe('Source template ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
            })
            .describe(
              'Source template properties. Use when `sourceType` = `SOURCE_TEMPLATE`.'
            ),
        }),
      ])
    )
    .describe('Branch to update.'),
});
export const UpdateBranchResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Branch ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'Revision number, which increments by 1 each time the branch is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the branch.'
      )
      .optional()
      .nullable(),
    type: z
      .enum(['USER', 'TECHNICAL', 'ORIGINAL_BRANCH'])
      .describe('Branch type.')
      .optional(),
    name: z.string().describe('Branch name.').max(50).optional().nullable(),
    sourceType: z
      .enum(['SOURCE_BRANCH'])
      .describe(
        "Source type for this branch's content. Must be aligned with the corresponding source properties below."
      )
      .optional(),
    lastPublishDate: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe('Date and time the branch was last published.')
      .optional()
      .nullable(),
    editorType: z
      .object({
        editorTypeOptions: z
          .enum(['STUDIO', 'CLASSIC'])
          .describe('Wix editor types.')
          .optional(),
      })
      .describe('Wix editor type.')
      .optional(),
    default: z
      .boolean()
      .describe(
        'Whether this branch is the default branch. The default branch is used for API operations when no branch ID is specified.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the branch was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the branch was last updated.')
      .optional()
      .nullable(),
    extendedFields: z
      .object({
        namespaces: z
          .record(z.string(), z.record(z.string(), z.any()))
          .describe(
            'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
          )
          .optional(),
      })
      .describe(
        'Custom field data for the branch.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
      )
      .optional(),
    tags: z
      .object({
        tags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
          )
          .optional(),
      })
      .describe('Public tags assigned to the branch.')
      .optional(),
  }),
  z.xor([
    z.object({
      sourceBranchProperties: z.never().optional(),
      sourceTemplateProperties: z.never().optional(),
    }),
    z.object({
      sourceTemplateProperties: z.never().optional(),
      sourceBranchProperties: z
        .object({
          branchId: z
            .string()
            .describe('Source branch ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          branchRevision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number of the source branch when this branch was created. To prevent conflicting changes, the current revision must be passed when updating the branch.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Source branch properties. Use when `sourceType` = `SOURCE_BRANCH`.'
        ),
    }),
    z.object({
      sourceBranchProperties: z.never().optional(),
      sourceTemplateProperties: z
        .object({
          templateId: z
            .string()
            .describe('Source template ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
        })
        .describe(
          'Source template properties. Use when `sourceType` = `SOURCE_TEMPLATE`.'
        ),
    }),
  ])
);
export const DeleteBranchRequest = z.object({
  branchId: z
    .string()
    .describe('ID of the branch to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteBranchResponse = z.object({});
export const QueryBranchesRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object.\n\nLearn more about [filtering](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#filters).'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor paging options.\n\nLearn more about [cursor paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#cursor-paging).'
            ),
        }),
      ])
    )
    .describe(
      'Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language) for more information.'
    ),
});
export const QueryBranchesResponse = z.object({
  branches: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Branch ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the branch is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the branch.'
            )
            .optional()
            .nullable(),
          type: z
            .enum(['USER', 'TECHNICAL', 'ORIGINAL_BRANCH'])
            .describe('Branch type.')
            .optional(),
          name: z
            .string()
            .describe('Branch name.')
            .max(50)
            .optional()
            .nullable(),
          sourceType: z
            .enum(['SOURCE_BRANCH'])
            .describe(
              "Source type for this branch's content. Must be aligned with the corresponding source properties below."
            )
            .optional(),
          lastPublishDate: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe('Date and time the branch was last published.')
            .optional()
            .nullable(),
          editorType: z
            .object({
              editorTypeOptions: z
                .enum(['STUDIO', 'CLASSIC'])
                .describe('Wix editor types.')
                .optional(),
            })
            .describe('Wix editor type.')
            .optional(),
          default: z
            .boolean()
            .describe(
              'Whether this branch is the default branch. The default branch is used for API operations when no branch ID is specified.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the branch was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the branch was last updated.')
            .optional()
            .nullable(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe(
              'Custom field data for the branch.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
            )
            .optional(),
          tags: z
            .object({
              tags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                )
                .optional(),
            })
            .describe('Public tags assigned to the branch.')
            .optional(),
        }),
        z.xor([
          z.object({
            sourceBranchProperties: z.never().optional(),
            sourceTemplateProperties: z.never().optional(),
          }),
          z.object({
            sourceTemplateProperties: z.never().optional(),
            sourceBranchProperties: z
              .object({
                branchId: z
                  .string()
                  .describe('Source branch ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                branchRevision: z
                  .string()
                  .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                  .describe(
                    'Revision number of the source branch when this branch was created. To prevent conflicting changes, the current revision must be passed when updating the branch.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Source branch properties. Use when `sourceType` = `SOURCE_BRANCH`.'
              ),
          }),
          z.object({
            sourceBranchProperties: z.never().optional(),
            sourceTemplateProperties: z
              .object({
                templateId: z
                  .string()
                  .describe('Source template ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
              })
              .describe(
                'Source template properties. Use when `sourceType` = `SOURCE_TEMPLATE`.'
              ),
          }),
        ])
      )
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Metadata for the paginated results.')
    .optional(),
});
export const SetDefaultBranchRequest = z.object({
  branchId: z
    .string()
    .describe('Branch ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const SetDefaultBranchResponse = z.object({
  branch: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Branch ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the branch is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the branch.'
          )
          .optional()
          .nullable(),
        type: z
          .enum(['USER', 'TECHNICAL', 'ORIGINAL_BRANCH'])
          .describe('Branch type.')
          .optional(),
        name: z.string().describe('Branch name.').max(50).optional().nullable(),
        sourceType: z
          .enum(['SOURCE_BRANCH'])
          .describe(
            "Source type for this branch's content. Must be aligned with the corresponding source properties below."
          )
          .optional(),
        lastPublishDate: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe('Date and time the branch was last published.')
          .optional()
          .nullable(),
        editorType: z
          .object({
            editorTypeOptions: z
              .enum(['STUDIO', 'CLASSIC'])
              .describe('Wix editor types.')
              .optional(),
          })
          .describe('Wix editor type.')
          .optional(),
        default: z
          .boolean()
          .describe(
            'Whether this branch is the default branch. The default branch is used for API operations when no branch ID is specified.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the branch was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the branch was last updated.')
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for the branch.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        tags: z
          .object({
            tags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe('Public tags assigned to the branch.')
          .optional(),
      }),
      z.xor([
        z.object({
          sourceBranchProperties: z.never().optional(),
          sourceTemplateProperties: z.never().optional(),
        }),
        z.object({
          sourceTemplateProperties: z.never().optional(),
          sourceBranchProperties: z
            .object({
              branchId: z
                .string()
                .describe('Source branch ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              branchRevision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number of the source branch when this branch was created. To prevent conflicting changes, the current revision must be passed when updating the branch.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Source branch properties. Use when `sourceType` = `SOURCE_BRANCH`.'
            ),
        }),
        z.object({
          sourceBranchProperties: z.never().optional(),
          sourceTemplateProperties: z
            .object({
              templateId: z
                .string()
                .describe('Source template ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
            })
            .describe(
              'Source template properties. Use when `sourceType` = `SOURCE_TEMPLATE`.'
            ),
        }),
      ])
    )
    .describe('Updated branch.')
    .optional(),
});
export const GetDefaultBranchRequest = z.object({});
export const GetDefaultBranchResponse = z.object({
  branch: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Branch ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the branch is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the branch.'
          )
          .optional()
          .nullable(),
        type: z
          .enum(['USER', 'TECHNICAL', 'ORIGINAL_BRANCH'])
          .describe('Branch type.')
          .optional(),
        name: z.string().describe('Branch name.').max(50).optional().nullable(),
        sourceType: z
          .enum(['SOURCE_BRANCH'])
          .describe(
            "Source type for this branch's content. Must be aligned with the corresponding source properties below."
          )
          .optional(),
        lastPublishDate: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe('Date and time the branch was last published.')
          .optional()
          .nullable(),
        editorType: z
          .object({
            editorTypeOptions: z
              .enum(['STUDIO', 'CLASSIC'])
              .describe('Wix editor types.')
              .optional(),
          })
          .describe('Wix editor type.')
          .optional(),
        default: z
          .boolean()
          .describe(
            'Whether this branch is the default branch. The default branch is used for API operations when no branch ID is specified.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the branch was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the branch was last updated.')
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for the branch.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        tags: z
          .object({
            tags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe('Public tags assigned to the branch.')
          .optional(),
      }),
      z.xor([
        z.object({
          sourceBranchProperties: z.never().optional(),
          sourceTemplateProperties: z.never().optional(),
        }),
        z.object({
          sourceTemplateProperties: z.never().optional(),
          sourceBranchProperties: z
            .object({
              branchId: z
                .string()
                .describe('Source branch ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              branchRevision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number of the source branch when this branch was created. To prevent conflicting changes, the current revision must be passed when updating the branch.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Source branch properties. Use when `sourceType` = `SOURCE_BRANCH`.'
            ),
        }),
        z.object({
          sourceBranchProperties: z.never().optional(),
          sourceTemplateProperties: z
            .object({
              templateId: z
                .string()
                .describe('Source template ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
            })
            .describe(
              'Source template properties. Use when `sourceType` = `SOURCE_TEMPLATE`.'
            ),
        }),
      ])
    )
    .describe('Default branch.')
    .optional(),
});
export const BulkUpdateBranchTagsRequest = z.object({
  branchIds: z.array(z.string()).min(1).max(100),
  options: z
    .object({
      assignTags: z
        .object({
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to assign to the branches.')
        .optional(),
      unassignTags: z
        .object({
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to unassign from the branches.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateBranchTagsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible to provide. For example, when failing to create an item."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action succeeded for this item. When `false`, the `error` field provides failure details.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe(
                'Details about the error if the operation failed for this item.'
              )
              .optional(),
          })
          .describe(
            'Metadata about the result for a specific branch, including whether the operation succeeded.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without detailed error information because the detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe(
      'Metadata for the bulk operation, including counts of successes and failures.'
    )
    .optional(),
});
export const BulkUpdateBranchTagsByFilterRequest = z.object({
  filter: z
    .record(z.string(), z.any())
    .describe(
      'Filter to select which branches to update. See [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language) for more information.'
    ),
  options: z
    .object({
      assignTags: z
        .object({
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to assign to the branches.')
        .optional(),
      unassignTags: z
        .object({
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to unassign from the branches.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateBranchTagsByFilterResponse = z.object({
  jobId: z
    .string()
    .describe(
      'Job ID. Pass this ID to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/get-async-job) to retrieve the status of the asynchronous operation.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
